import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/Barco.css";
import barcoLogo from "../../assets/images/partners/Barco.png";

const controlFeatures = [
  "Real-time 4K video wall management with operator-assignable layouts and automated event-triggered presets",
  "Redundant signal routing eliminating single points of failure in mission-critical 24/7 control environments",
  "Integration with SCADA, BMS, traffic management, and emergency dispatch software for unified situational awareness",
  "Operator ergonomics designed for extended monitoring — including adjustable viewing angles and low-glare display surfaces",
  "Remote monitoring via Barco Control Room Manager — adjusting layouts and routing from any networked workstation",
];

const meetingProducts = [
  { name: "Barco ClickShare Conference", desc: "One-click wireless conferencing system — auto-detects meeting platform (Teams, Zoom, Webex) and connects room camera, audio, and display without cable management or app installation." },
  { name: "Barco ClickShare Bar", desc: "Integrated soundbar camera and wireless collaboration in one sleek bar-form device — designed for small and medium conference rooms with Microsoft Teams and Zoom Rooms certification." },
  { name: "Barco UniSee LCD Video Walls", desc: "Near-seamless LCD video wall solution for executive briefing centers and lobby installations — featuring Barco's zero-gap design and integrated content management." },
  { name: "Barco TransForm N", desc: "Video wall processor for large-format meeting room and boardroom displays — enabling dynamic window management, multi-source display, and touchscreen interaction." },
];

const enterpriseItems = [
  { icon: "🎛️", title: "Command & Control Centers", desc: "24/7 operations centers for utilities, transportation, and emergency management — with redundant video wall systems designed for continuous high-stakes monitoring environments." },
  { icon: "🏥", title: "Surgical & Clinical Imaging", desc: "High-brightness medical displays certified to DICOM standards — supporting diagnostic imaging, surgical visualization, and radiology workstation deployments in clinical environments." },
  { icon: "🎬", title: "Cinema & Entertainment", desc: "Barco cinema projectors serve over 40% of global cinema screens — including laser projection upgrades, 3D cinema systems, and premium large-format PLF presentation environments." },
  { icon: "🏢", title: "Corporate Communications", desc: "Executive boardrooms, digital signage networks, and corporate broadcast studios with Barco's managed display ecosystem delivering content from a single cloud management platform." },
  { icon: "🔬", title: "Research & Simulation", desc: "Multi-wall visualization environments for scientific research centers, data analytics facilities, and simulation training — with pixel-perfect synchronization across large display arrays." },
  { icon: "🎓", title: "Higher Education", desc: "Lecture capture, hybrid classroom displays, and campus digital signage — with ClickShare enabling any BYOD device to present wirelessly without IT-managed cable infrastructure." },
];

const timelineItems = [
  { year: "1934", title: "Founded in Kortrijk, Belgium", desc: "Barco was established as a manufacturer of radio broadcast equipment — beginning a nine-decade journey of visual technology innovation." },
  { year: "1980s", title: "Projection Pioneering", desc: "Barco developed some of the world's first data projectors — enabling large-format presentation before the personal computer era." },
  { year: "2000s", title: "Digital Cinema Leadership", desc: "Barco became the global leader in digital cinema projection — equipping theaters worldwide as the industry transitioned from 35mm film to digital projection systems." },
  { year: "2010s", title: "Healthcare Imaging Innovation", desc: "Barco's medical display division earned global recognition for DICOM diagnostic displays — setting standards for clinical image quality that radiologists trust." },
  { year: "2018", title: "ClickShare — Wireless Collaboration", desc: "ClickShare redefined meeting room technology — enabling any-device wireless presentation and conferencing that became the standard for hybrid-ready conference rooms." },
  { year: "Now", title: "AI-Enhanced Visualization", desc: "Barco's current generation integrates AI for intelligent layout automation, predictive maintenance, and adaptive display optimization — defining the next era of enterprise visualization." },
];

const industries = [
  { icon: "🏛️", name: "Government & Defense", desc: "Classified operations centers, situation rooms, and emergency management facilities with Barco's security-certified visualization systems." },
  { icon: "⚡", name: "Energy & Utilities", desc: "Power grid monitoring, pipeline control rooms, and smart energy management centers requiring continuous 24/7 high-reliability display infrastructure." },
  { icon: "🏥", name: "Healthcare", desc: "Hospital command centers, radiology suites, surgical theaters, and clinical waiting areas with medical-grade Barco display technology." },
  { icon: "🎬", name: "Entertainment", desc: "Cinema complexes, theme parks, live event venues, and broadcast facilities with Barco projection and LED solutions." },
  { icon: "✈️", name: "Transportation", desc: "Air traffic control centers, transit management facilities, port operations, and passenger information systems with Barco video wall solutions." },
  { icon: "🏢", name: "Enterprise", desc: "Executive boardrooms, digital employee communications, corporate lobbies, and hybrid workplace collaboration rooms." },
  { icon: "🎓", name: "Education", desc: "University lecture halls, research visualization centers, eSports arenas, and campus-wide digital signage networks." },
  { icon: "🔬", name: "Science & Research", desc: "Scientific visualization environments, simulation facilities, and data analytics centers with multi-wall display arrays." },
];

const reasons = [
  { num: "01", title: "Barco Authorized Partner", desc: "Direct partnership with Barco providing access to full product catalog, pre-sales technical support, and partner pricing for visualization and collaboration projects." },
  { num: "02", title: "Meeting Room Design Expertise", desc: "ALG Data Guard designs ClickShare deployments integrated with your existing UC platform (Teams Rooms, Zoom Rooms, Webex) — ensuring every room works flawlessly from day one." },
  { num: "03", title: "AV/IT Integration Skills", desc: "Video wall deployments, control room design, and enterprise AV require IT-side integration. Our team bridges the AV and IT domains for complete end-to-end solutions." },
  { num: "04", title: "Post-Deployment Management", desc: "Content management, firmware coordination, room health monitoring, and on-site service coordination through ALG Data Guard's managed AV services program." },
];

const faqData = [
  { q: "What is Barco ClickShare and how does it work for hybrid meetings?", a: "Barco ClickShare is a wireless conferencing and presentation system designed for conference rooms — enabling any meeting participant to share content wirelessly and connect the room's camera, microphone, and display to their meeting platform (Microsoft Teams, Zoom, or Cisco Webex) without cable connections or manual configuration. The ClickShare Base Unit is installed in the conference room connected to the room's display, camera, and audio equipment. Meeting participants either plug in a ClickShare Button (USB dongle) to their laptop for one-click connection, or use the ClickShare app on their laptop or mobile device. When connected, the room's camera and audio automatically become the active camera and microphone in their meeting — eliminating the need for a dedicated room PC, Teams Rooms hardware, or Zoom Rooms setup. ClickShare Conference is ideal for organizations wanting wireless bring-your-own-device meeting rooms without committing to a single platform's room system. ALG Data Guard designs ClickShare deployments across office portfolios — standardizing conference rooms for consistent hybrid meeting experiences regardless of which meeting platform participants use." },
  { q: "What is the difference between Barco ClickShare and a Microsoft Teams Rooms or Zoom Rooms system?", a: "Microsoft Teams Rooms (MTR) and Zoom Rooms are dedicated room computing systems — a permanent Teams or Zoom meeting environment with a fixed room account that participants join from a room console. They offer consistent in-room experience optimized for one platform. Barco ClickShare Conference is a bring-your-own-device (BYOD) approach — the room has no permanent meeting account; instead, the host connects their laptop and uses their own meeting account with the room's camera and audio. Each has advantages: Teams Rooms and Zoom Rooms work better for organizations standardized on one platform and wanting calendar-integrated room booking with one-touch join. ClickShare works better for organizations that host participants from multiple companies or platforms (guests using Zoom while you're on Teams), or want flexibility without platform commitment. Many organizations deploy both — Teams Rooms in executive boardrooms and ClickShare in standard conference rooms. ALG Data Guard helps organizations evaluate which approach fits each room type and usage pattern." },
  { q: "What control room video wall products does Barco offer?", a: "Barco is a global leader in control room visualization. Key products include: Barco UniSee — a near-seamless LCD video wall system using ultra-narrow bezel panels for 24/7 continuous operation in critical operations centers. The OpSpace software platform manages multi-operator video wall environments, allowing each operator to control their own workspace while sharing a common display surface. Barco CMS (Controller Management System) provides intelligent video wall routing and source management. For direct-view LED applications in large control rooms, Barco's LED wall solutions provide extremely high brightness for environments with uncontrolled lighting. ALG Data Guard works with Barco's control room division on complex design projects — including redundancy requirements, operator ergonomic standards, source signal routing design, and integration with SCADA, BMS, or other operational software displayed on the video wall." },
  { q: "What Barco medical display products does ALG Data Guard provide?", a: "Barco's medical display portfolio includes DICOM diagnostic workstation displays at various resolutions (2MP, 3MP, 5MP, 12MP) for different diagnostic applications — from general radiology to mammagraphy to pathology. Barco's Nio and Coronis Uniti display lines are FDA-cleared diagnostic displays with integrated quality assurance software (MediCal QAWeb) that automates daily AAPM QC testing and remote compliance reporting. For surgical environments, Barco's nexxis platform enables digital OR visualization — routing surgical video sources to display systems throughout the operating room with minimal latency. For clinical communication displays (patient-facing, corridor, waiting room), Barco offers Silex networked display systems managed centrally. ALG Data Guard provides Barco medical display deployments for hospitals, imaging centers, and outpatient facilities — with proper DICOM calibration, QAWeb enrollment, and hospital IT network integration." },
  { q: "How does ALG Data Guard support Barco deployments after installation?", a: "ALG Data Guard provides comprehensive post-installation Barco support: ClickShare fleet management through Barco Management Suite — monitoring all ClickShare devices, pushing firmware updates, configuring meeting room templates, and tracking room utilization analytics. Video wall health monitoring — Barco video wall controllers and display panels provide remote health telemetry; ALG Data Guard monitors panel health, backlight hours, and temperature for proactive maintenance scheduling before failures occur. Content management services for digital signage applications — updating digital communications, scheduling content rotations, and managing multi-location content distribution. On-site service coordination for hardware replacements through our Barco partner relationship. Annual display calibration services for medical displays — ensuring diagnostic displays maintain DICOM compliance throughout their operational life. Expansion planning — as your meeting room portfolio grows or your control room requirements change, ALG Data Guard provides design and integration services to extend your Barco infrastructure." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function Barco() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="ba-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={barcoLogo} alt="Barco" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Visualization & Collaboration Technology Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Barco</h1>
            <p className="pd-identity-desc">
              Barco is a global technology company designing visualization and collaboration experiences for the most demanding environments on earth — from cinema projection systems in 40% of the world's theaters to 24/7 command and control video walls in critical infrastructure operations centers, and diagnostic imaging displays in hospitals trusting DICOM-calibrated accuracy. Barco ClickShare is the world's leading wireless conferencing platform for hybrid meeting rooms. ALG Data Guard is an authorized Barco partner providing design, deployment, and managed services for the complete Barco portfolio across healthcare, enterprise, government, and entertainment.
            </p>
          </div>
        </div>
      </div>

      {/* ── Cinematic Hero ── */}
      <section className="ba-hero">
        <img
          src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1800&q=85"
          alt="Barco visualization technology"
          className="ba-hero__img"
        />
        <div className="ba-hero__overlay" aria-hidden="true" />
        <div className="ba-hero__inner">
          <div className="ba-hero__issue">Barco × ALG Data Guard — Authorized Partner</div>
          <h2 className="ba-editorial">
            See Everything.<br />Control Everything.<br /><span style={{ color:"var(--ba-gold)" }}>Visualize.</span>
          </h2>
          <p className="ba-hero__sub">
            Barco designs the visual experiences that power mission-critical operations, transform meeting rooms, and define premium entertainment — from the operating room to the control room, from the conference room to the cinema. ALG Data Guard brings Barco's premium visualization portfolio to enterprises, healthcare systems, and government organizations across the region.
          </p>
          <div className="ba-hero__actions">
            <button className="ba-btn ba-btn--gold" onClick={() => navigate("/contact")}>
              Design My Visual Environment <ArrowIcon />
            </button>
            <button className="ba-btn ba-btn--ghost" onClick={() => navigate("/contact")}>
              Request Demo
            </button>
          </div>
          <div className="ba-hero__facts">
            <div>
              <div className="ba-hero__fact-val">40%</div>
              <div className="ba-hero__fact-label">Global Cinema Screens</div>
            </div>
            <div>
              <div className="ba-hero__fact-val">90+</div>
              <div className="ba-hero__fact-label">Years of Innovation</div>
            </div>
            <div>
              <div className="ba-hero__fact-val">100+</div>
              <div className="ba-hero__fact-label">Countries</div>
            </div>
            <div>
              <div className="ba-hero__fact-val">3,300+</div>
              <div className="ba-hero__fact-label">Team Members</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Visual Collaboration ── */}
      <section className="ba-collab">
        <div className="ba-shell">
          <div className="ba-collab__inner">
            <img
              src="https://images.unsplash.com/photo-1577962917302-cd874c4e31d2?auto=format&fit=crop&w=900&q=80"
              alt="Barco ClickShare visual collaboration"
              className="ba-collab__img"
            />
            <div>
              <div className="ba-collab__overline">Visual Collaboration</div>
              <h2 className="ba-heading ba-collab__title">The Meeting Room Has Evolved. Has Yours?</h2>
              <p className="ba-body ba-collab__body">
                Barco ClickShare has redefined what a meeting room is. Where conference rooms once required dedicated hardware, fixed cable connections, and IT-managed room systems — ClickShare delivers one-click wireless conferencing from any device on any platform. Participants walk in, connect, and the room's camera, microphone, and display instantly become theirs.
              </p>
              <div className="ba-collab__pull-quote">
                <p>"The best meeting room technology is the one participants don't think about — it just works."</p>
              </div>
              <p className="ba-body ba-collab__body">
                ALG Data Guard designs ClickShare deployments that integrate seamlessly with Microsoft Teams, Zoom, and Cisco Webex — standardizing your conference room portfolio for consistent hybrid meeting experiences regardless of which platform hosts the meeting.
              </p>
              <div className="ba-collab__solutions">
                {["One-click wireless conferencing from BYOD laptops", "Platform-agnostic — Teams, Zoom, Google Meet, Webex", "ClickShare Bar for small rooms — camera, audio, wireless in one device", "Fleet management through Barco Management Suite", "Meeting analytics and room utilization reporting"].map(s => (
                  <div key={s} className="ba-collab-sol">{s}</div>
                ))}
              </div>
              <button className="ba-btn ba-btn--blue" onClick={() => navigate("/contact")}>
                Design My Meeting Rooms <ArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Control Rooms — Full Bleed ── */}
      <section className="ba-control">
        <img
          src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1800&q=85"
          alt="Barco control room visualization"
          className="ba-control__img"
        />
        <div className="ba-control__overlay" aria-hidden="true" />
        <div className="ba-control__inner">
          <div className="ba-marker ba-marker--light">Control Room Solutions</div>
          <h2 className="ba-heading ba-control__title" style={{ color:"#fff" }}>
            When Decisions Matter Most,<br />Your Display Cannot Fail.
          </h2>
          <p className="ba-control__body">
            Barco's control room video wall solutions are the standard for critical operations centers where 24/7 reliability, redundancy, and real-time situational awareness are non-negotiable. From power grid operations centers to air traffic management facilities, Barco's video wall systems and operator management software define the environment where critical decisions are made.
          </p>
          <div className="ba-control__features">
            {controlFeatures.map(f => (
              <div key={f} className="ba-ctrl-feat">
                <div className="ba-ctrl-feat__dot" />
                <div className="ba-ctrl-feat__text">{f}</div>
              </div>
            ))}
          </div>
          <button className="ba-btn ba-btn--gold" onClick={() => navigate("/contact")}>
            Control Room Consultation <ArrowIcon />
          </button>
        </div>
      </section>

      {/* ── Healthcare Imaging ── */}
      <section className="ba-health">
        <div className="ba-shell">
          <div className="ba-health__inner">
            <div>
              <div className="ba-marker ba-marker--dark">Healthcare Imaging</div>
              <h2 className="ba-heading ba-health__title">Clinical-Grade Displays Built on Diagnostic Accuracy</h2>
              <p className="ba-body ba-health__body">
                In radiology, oncology, and pathology, display accuracy isn't a feature — it's a clinical requirement. Barco's diagnostic imaging displays are DICOM-calibrated, FDA-cleared, and trusted by radiologists worldwide for the image quality that enables confident diagnosis. Every Barco medical display includes MediCal QAWeb — an automated quality assurance platform performing daily AAPM-standard calibration verification and generating compliance documentation for JCAHO and ACR audits.
              </p>
              <p className="ba-body ba-health__body">
                ALG Data Guard provides complete Barco medical display deployments — from radiology workstation displays through surgical OR visualization systems, calibrated to DICOM standards with IT network integration for PACS connectivity.
              </p>
              <div className="ba-health__stats">
                <div className="ba-health-stat">
                  <div className="ba-health-stat__val">12MP</div>
                  <div className="ba-health-stat__label">Maximum Resolution</div>
                </div>
                <div className="ba-health-stat">
                  <div className="ba-health-stat__val">5,000:1</div>
                  <div className="ba-health-stat__label">Contrast Ratio</div>
                </div>
                <div className="ba-health-stat">
                  <div className="ba-health-stat__val">Auto</div>
                  <div className="ba-health-stat__label">DICOM Calibration</div>
                </div>
                <div className="ba-health-stat">
                  <div className="ba-health-stat__val">FDA</div>
                  <div className="ba-health-stat__label">510(k) Cleared</div>
                </div>
              </div>
              <button className="ba-btn ba-btn--blue" onClick={() => navigate("/contact")}>
                Healthcare Display Consultation <ArrowIcon />
              </button>
            </div>
            <img
              src="https://images.unsplash.com/photo-1559757175-0eb30cd8c063?auto=format&fit=crop&w=900&q=80"
              alt="Barco healthcare medical imaging displays"
              className="ba-health__img"
            />
          </div>
        </div>
      </section>

      {/* ── Cinema & Entertainment — Full Bleed ── */}
      <section className="ba-cinema">
        <img
          src="https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=1800&q=85"
          alt="Barco cinema projection entertainment"
          className="ba-cinema__img"
        />
        <div className="ba-cinema__overlay" aria-hidden="true" />
        <div className="ba-cinema__inner">
          <div className="ba-marker ba-marker--light">Cinema & Entertainment</div>
          <h2 className="ba-heading ba-cinema__heading" style={{ color:"#fff" }}>
            Powering 40% of the<br />World's Cinema Screens.
          </h2>
          <p className="ba-cinema__body">
            Barco cinema projectors define the global cinema experience — with laser and lamp projection solutions serving major cinema chains and premium large-format theaters worldwide. Barco's cinema portfolio extends beyond projection to include the full digital cinema chain: media servers, screen management systems, and the Barco Alchemy media server powering the world's most advanced cinema experiences.
          </p>
          <div className="ba-cinema__specs">
            <div className="ba-cinema-spec">
              <div className="ba-cinema-spec__val">60,000+</div>
              <div className="ba-cinema-spec__label">Cinema Projectors Installed</div>
            </div>
            <div className="ba-cinema-spec">
              <div className="ba-cinema-spec__val">75,000 lm</div>
              <div className="ba-cinema-spec__label">Peak Laser Brightness</div>
            </div>
            <div className="ba-cinema-spec">
              <div className="ba-cinema-spec__val">4K</div>
              <div className="ba-cinema-spec__label">DCI-Certified Resolution</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Smart Meeting Rooms ── */}
      <section className="ba-meeting">
        <div className="ba-shell">
          <div className="ba-meeting__inner">
            <img
              src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=900&q=80"
              alt="Barco smart meeting room technology"
              className="ba-meeting__img"
            />
            <div>
              <div className="ba-marker ba-marker--dark">Smart Meeting Rooms</div>
              <h2 className="ba-heading ba-meeting__title">Conference Rooms That Work the Way Your Team Does</h2>
              <p className="ba-body ba-meeting__body">
                Every Barco ClickShare product is designed around a single principle: the meeting room should work for the people in it, not the other way around. No cable hunting, no platform switching, no IT call before the client presentation — just click, connect, and collaborate.
              </p>
              <p className="ba-body ba-meeting__body">
                ALG Data Guard deploys ClickShare across complete office portfolios — from huddle rooms to executive boardrooms — with Barco Management Suite providing centralized monitoring, firmware management, and room analytics for your entire ClickShare deployment.
              </p>
              <div className="ba-meeting__products">
                {meetingProducts.map(p => (
                  <div key={p.name} className="ba-meet-product">
                    <div className="ba-meet-product__name">{p.name}</div>
                    <p className="ba-meet-product__desc">{p.desc}</p>
                  </div>
                ))}
              </div>
              <button className="ba-btn ba-btn--blue" onClick={() => navigate("/contact")}>
                Deploy ClickShare <ArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Enterprise Applications ── */}
      <section className="ba-enterprise">
        <div className="ba-shell">
          <div className="ba-enterprise__head">
            <div className="ba-marker ba-marker--light" style={{ justifyContent:"center" }}>Enterprise Applications</div>
            <h2 className="ba-heading" style={{ color:"#fff", marginBottom:"1rem" }}>Barco Across Every Enterprise Environment</h2>
            <p className="ba-body" style={{ color:"var(--ba-light)", margin:"0 auto", maxWidth:"600px" }}>From mission-critical control rooms to premium cinema experiences, Barco visualization technology defines the performance standard in every industry vertical it serves.</p>
          </div>
          <div className="ba-enterprise__grid">
            {enterpriseItems.map(item => (
              <div key={item.title} className="ba-ent-item">
                <span className="ba-ent-item__icon">{item.icon}</span>
                <h3 className="ba-ent-item__title">{item.title}</h3>
                <p className="ba-ent-item__desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Innovation Timeline ── */}
      <section className="ba-timeline">
        <div className="ba-shell">
          <div className="ba-timeline__head">
            <div className="ba-marker ba-marker--dark" style={{ justifyContent:"center" }}>Innovation Timeline</div>
            <h2 className="ba-heading" style={{ color:"var(--ba-text)", marginBottom:"1rem" }}>Nine Decades of Visual Innovation</h2>
            <p className="ba-body" style={{ margin:"0 auto", maxWidth:"560px" }}>Barco's history is the history of enterprise visualization — from radio broadcasting to laser cinema projection, from analog displays to AI-enhanced video walls.</p>
          </div>
          <div className="ba-timeline__track">
            {timelineItems.map(item => (
              <div key={item.year} className="ba-tl-item">
                <div className="ba-tl-item__year">{item.year}</div>
                <div className="ba-tl-item__content">
                  <div className="ba-tl-item__dot" />
                  <h3 className="ba-tl-item__title">{item.title}</h3>
                  <p className="ba-tl-item__desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="ba-industries">
        <div className="ba-shell">
          <div className="ba-industries__head">
            <div className="ba-marker ba-marker--dark" style={{ justifyContent:"center" }}>Industries</div>
            <h2 className="ba-heading">Barco in Every Critical Environment</h2>
          </div>
        </div>
        <div className="ba-industries__grid">
          {industries.map(ind => (
            <div key={ind.name} className="ba-ind-item">
              <span className="ba-ind-item__icon">{ind.icon}</span>
              <h3 className="ba-ind-item__name">{ind.name}</h3>
              <p className="ba-ind-item__desc">{ind.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Why ALG ── */}
      <section className="ba-why">
        <div className="ba-shell">
          <div className="ba-why__inner">
            <div>
              <div className="ba-marker ba-marker--light">Why ALG Data Guard</div>
              <h2 className="ba-heading ba-why__title" style={{ color:"#fff" }}>
                Barco Expertise,<br />Delivered End to End.
              </h2>
              <p className="ba-why__body">
                Barco technology is only as effective as its integration into your specific environment. Control rooms require redundancy engineering. Meeting rooms require UC platform integration. Medical displays require DICOM calibration. ALG Data Guard brings the cross-domain expertise to deploy Barco technology correctly — from the initial design brief through long-term management.
              </p>
              <div className="ba-why__reasons">
                {reasons.map(r => (
                  <div key={r.num} className="ba-reason">
                    <div className="ba-reason__num">{r.num}</div>
                    <div>
                      <div className="ba-reason__title">{r.title}</div>
                      <p className="ba-reason__desc">{r.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2.5rem" }}>
                <button className="ba-btn ba-btn--gold" onClick={() => navigate("/contact")}>
                  Start My Barco Project <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80"
              alt="Barco enterprise visual environment"
              className="ba-why__img"
            />
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="ba-faq">
        <div className="ba-shell">
          <div className="ba-faq__head">
            <div className="ba-marker ba-marker--dark" style={{ justifyContent:"center" }}>FAQs</div>
            <h2 className="ba-heading" style={{ color:"var(--ba-text)" }}>Barco Questions, Answered</h2>
            <p className="ba-body" style={{ margin:"0 auto", maxWidth:"560px" }}>Expert answers from ALG Data Guard's Barco-certified specialists about ClickShare, video walls, medical displays, and enterprise visualization.</p>
          </div>
          <div className="ba-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`ba-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="ba-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="ba-faq-item__q">{faq.q}</span>
                  <span className="ba-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="ba-faq-item__answer"><div className="ba-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA — Final Cinematic ── */}
      <section className="ba-cta">
        <img
          src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=1800&q=85"
          alt="Premium visualization experience"
          className="ba-cta__img"
        />
        <div className="ba-cta__overlay" aria-hidden="true" />
        <div className="ba-cta__inner">
          <div className="ba-marker ba-marker--light">Get Started</div>
          <h2 className="ba-heading ba-cta__title" style={{ color:"#fff" }}>
            Transform Your Visual<br />Environments with Barco.
          </h2>
          <p className="ba-cta__sub">
            Whether you're upgrading a conference room portfolio with ClickShare, deploying a mission-critical control room video wall, implementing diagnostic imaging displays for a radiology department, or designing a cinema projection system — ALG Data Guard delivers Barco's premium visualization technology from design through ongoing management.
          </p>
          <div className="ba-cta__actions">
            <button className="ba-btn ba-btn--gold" onClick={() => navigate("/contact")}>
              Begin My Barco Project <ArrowIcon />
            </button>
            <button className="ba-btn ba-btn--ghost" onClick={() => navigate("/contact")}>
              Request a Demo
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
